import { Link } from 'found';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Badge, Card, CardBody, CardFooter, CardSubtitle, CardText, CardTitle,
} from 'reactstrap';

import TradeRuleTooltip from 'src/components/TradeRule/TradeRuleTooltip';
import { APIConfig } from 'src/config';
import Time from 'src/enosikit/components/Time/components/Time';
import {
  TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL,
} from 'src/util/constants';
import { getNodesFromEdges } from 'src/util/graphql';
import displayUserName from 'src/util/i18n/decorators/displayUserName';
import { getStringAndNumericFormattedDate } from 'src/util/i18n/helpers';
import { buildTradeRulePriceInformation } from 'src/util/i18n/tradeRule';
import { getPriceRange } from 'src/util/tradeRule';

/**
 * Renders a card component displaying trade rule information for community trades.
 * Shows trade rule details including meter information, proposal/acceptance timestamps,
 * price range, and navigation links to detailed trade rule view.
 * @param {import('react-intl').IntlShape} intl - i18n intl object
 * @param {object} buyer - Trader object representing the buyer
 * @param {object} seller - Trader object representing the seller
 * @param {TRADE_DIRECTION_BUY | TRADE_DIRECTION_SELL} tradeDirection - Trade direction
 * @returns {string | React.ReactNode} - meter label for Proposed trade rules cards.
 */
const getTradeRuleProposedMeterLabel = (intl, buyer, seller, tradeDirection) => {
  if (!buyer || !seller || !intl || !tradeDirection) return null;

  if (tradeDirection === TRADE_DIRECTION_BUY) {
    return intl.formatMessage(
      { id: 'trade_rule.trade_rule_active.proposed_trade_rule.buy.proposal.actors_detail', defaultMessage: '{counterparty} has proposed to buy from {actor}' },
      {
        actor: <strong>{displayUserName(intl, seller.user)}</strong>,
        counterparty: (
          <strong>
            {displayUserName(intl, buyer.user)}
          </strong>
        ),
      },
    );
  }

  return intl.formatMessage(
    { id: 'trade_rule.trade_rule_active.proposed_trade_rule.sell.proposal.actors_detail', defaultMessage: '{counterparty} has proposed to sell to {actor}' },
    {
      actor: displayUserName(intl, buyer.user),
      counterparty: displayUserName(intl, seller.user),
    },
  );
};

/**
 * Renders a card component displaying trade rule information for community trades.
 * Shows trade rule details including meter information, proposal/acceptance timestamps,
 * price range, and navigation links to detailed trade rule view.
 * @param {object} props - Component props
 * @param {string} props.tradeDirection - Trade direction (BUY or SELL)
 * @param {string} props.propertyId - UUID of the property containing the trade rule
 * @param {object} props.rule - TradeRule object from GraphQL (TradeRule type)
 * @param {string} props.rule.uuid - Unique identifier for the trade rule
 * @param {object} props.rule.meter - Meter object associated with the trade rule
 * @param {string} props.rule.meter.title - Display title of the meter
 * @param {number} props.rule.acceptedAt - Unix timestamp when rule was accepted
 * @param {object} props.rule.acceptedBy - User who accepted the rule (User type)
 * @param {string} props.rule.acceptedBy.uuid - UUID of accepting user
 * @param {string} props.rule.acceptedBy.email - Email of accepting user
 * @param {string} props.rule.acceptedBy.givenName - Given name of accepting user
 * @param {string} props.rule.acceptedBy.familyName - Family name of accepting user
 * @param {number} props.rule.proposedAt - Unix timestamp when rule was proposed
 * @param {object} props.rule.proposedBy - User who proposed the rule (User type)
 * @param {string} props.rule.proposedBy.uuid - UUID of proposing user
 * @param {string} props.rule.proposedBy.email - Email of proposing user
 * @param {string} props.rule.proposedBy.givenName - Given name of proposing user
 * @param {string} props.rule.proposedBy.familyName - Family name of proposing user
 * @param {object} props.rule.buyer - Trader object representing the buyer
 * @param {object} props.rule.seller - Trader object representing the seller
 * @param {object} props.rule.clauses - Connection object containing price clauses
 * @param {object} props.rule.priority
 * @param {string} [props.tradeRuleState=TRADE_RULE_STATE_ACCEPTED] - Trade rule state
 * @param {Function} props.actionButtonFn - Function to generate action buttons for the trade rule
 * @returns {React.ReactElement} Card component displaying trade rule information
 */
function ProposedTradeRule(props) {
  const {
    tradeDirection, propertyId, rule, actionButtonFn,
  } = props || {};
  const {
    uuid: ruleId, meter, proposedAt, proposedBy, buyer, seller, clauses,
  } = rule || {};
  const { title: tradePointTitle } = meter || {};
  const priceRange = getPriceRange(rule);
  const { maximum, minimum } = priceRange || {};
  const intl = useIntl();

  const clauseNodes = clauses ? getNodesFromEdges(clauses) : [];

  const furtherDetailsLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_active.proposed_trade_rule.buy.further_details', defaultMessage: 'further details' });
  const proposedLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_active.proposed_trade_rule.buy.proposed.label', defaultMessage: 'Proposed' });

  return (
    <Card className="mt-4 mb-4" key={`trade-rules-${ruleId}`}>
      <TradeRuleTooltip
        tradeDirection={tradeDirection}
        ruleId={ruleId}
        clauseNodes={clauseNodes}
      >
        {(cardBodyId) => (
          <CardBody id={cardBodyId} style={clauseNodes.length > 0 ? { cursor: 'help' } : undefined} tabIndex={0}>
            <CardTitle tag="h4">{tradePointTitle}</CardTitle>
            <CardSubtitle tag="h5" className="mb-2">{proposedLabel}</CardSubtitle>
            <CardText>
              {getTradeRuleProposedMeterLabel(intl, buyer, seller, tradeDirection)}
              <br />
              <FormattedMessage
                id="trade_rule.trade_rule_active.proposed_trade_rule.buy.rule.proposed"
                defaultMessage="Proposed by {actor} on {timestamp}"
                values={{
                  actor: <strong>{displayUserName(intl, proposedBy)}</strong>,
                  timestamp: <Time
                    child={getStringAndNumericFormattedDate(intl, proposedAt)}
                    unixTimestamp={proposedAt}
                  />,
                }}
              />
              <br />
              {buildTradeRulePriceInformation({
                detailsLink: <Link to={`/properties/${propertyId}/trade-rules/${ruleId}`}>{furtherDetailsLabel}</Link>,
                direction: tradeDirection,
                intl,
                maximum,
                minimum,
                mode: APIConfig().MODE,
              })}
              <br />
              <Link
                to={`/properties/${propertyId}/trade-rules/${ruleId}`}
              >
                <Badge color="mid">{ruleId}</Badge>
              </Link>
            </CardText>
          </CardBody>
        )}
      </TradeRuleTooltip>
      <CardFooter>{actionButtonFn(intl, rule)}</CardFooter>
    </Card>
  );
}

export default ProposedTradeRule;
